import { useRef, useState } from 'react';
import { errorMessage } from '@/core/errorMessage';
import { Camera, Trash2 } from 'lucide-react';
import { supabase } from '@/core/supabase';
import { useSession } from '@/core/session';
import { useT } from '@/core/i18n';
import { uploadFile, deleteFile } from '@/core/files';
import { prepareAvatar, AvatarImageError, AVATAR_ACCEPT } from '@/core/avatarImage';
import { Avatar, forgetAvatar } from './Avatar';
import { Button, Card, CardHeader } from './ui';
import { Spinner } from './Loader';

/* =============================================================================
 * Choosing your own picture
 * -----------------------------------------------------------------------------
 * Optional, and it stays optional. Nothing anywhere requires one, no screen
 * nags for one, and initials are what the whole application is designed around
 * — see Avatar.tsx. This is somebody choosing to be recognisable in a list, not
 * a field to be completed.
 *
 * The order of operations matters and is the opposite of the obvious one:
 *
 *   1. reduce the picture in the browser
 *   2. upload the reduced file
 *   3. point the profile at it
 *   4. delete whatever it pointed at before
 *
 * The old file goes last so that a failure anywhere above it leaves the person
 * with the picture they already had. Deleting first and failing at step two is
 * how somebody loses a photograph by trying to replace it.
 * ========================================================================== */

export function AvatarPicker() {
  const t = useT();
  const { profile, refresh } = useSession();
  const inputRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File | undefined) {
    if (!file || !profile) return;

    setError(null);
    setBusy(true);

    const previous = profile.avatarPath;

    try {
      /* Four megabytes off a phone camera becomes forty kilobytes here, before
         anything is sent. On a metered connection on a site that is the whole
         difference between a feature people use and one they abandon. */
      const reduced = await prepareAvatar(file);

      /* No employeeId. This is the login's picture, and a login need not be an
         employee at all — see migration 0119, which is what makes that
         permitted. */
      const uploaded = await uploadFile('avatar', reduced, { prefix: 'avatar' });

      const { error: saveError } = await supabase
        .from('user_profiles')
        .update({ avatar_path: uploaded.filePath })
        .eq('id', profile.id);

      if (saveError) throw new Error(saveError.message);

      /* Only now. Everything above has succeeded, so the person is never left
         between two pictures. A failure here costs a stale file on ImageKit,
         which is a bill, not a loss. */
      if (previous) {
        forgetAvatar(previous);
        try {
          await deleteFile(previous);
        } catch {
          /* Nothing worth showing. The new picture is already theirs. */
        }
      }

      forgetAvatar(uploaded.filePath);
      await refresh();
    } catch (caught) {
      /* AvatarImageError is this application's own — "That image is too
         large." and the like, written for whoever is holding the phone. Anything
         else is a fault and goes through the funnel. */
      setError(
        caught instanceof AvatarImageError
          ? caught.message
          : errorMessage(caught, 'change the photo'),
      );
    } finally {
      setBusy(false);
      // So choosing the same file twice in a row still fires a change event.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    if (!profile?.avatarPath) return;

    setError(null);
    setBusy(true);
    const going = profile.avatarPath;

    try {
      const { error: saveError } = await supabase
        .from('user_profiles')
        .update({ avatar_path: null })
        .eq('id', profile.id);

      if (saveError) throw new Error(saveError.message);

      forgetAvatar(going);
      try {
        await deleteFile(going);
      } catch {
        /* The profile no longer points at it, which is what was asked for. */
      }

      await refresh();
    } catch (caught) {
      setError(errorMessage(caught, 'change the photo'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('av.title')} subtitle={t('av.subtitle')} />

      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <Avatar name={profile?.fullName} path={profile?.avatarPath} size="xl" />

        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              icon={busy ? <Spinner size={14} /> : <Camera size={14} />}
              onClick={() => inputRef.current?.click()}
            >
              {profile?.avatarPath ? t('av.change') : t('av.choose')}
            </Button>

            {profile?.avatarPath && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                icon={<Trash2 size={14} />}
                onClick={() => void remove()}
              >
                {t('av.remove')}
              </Button>
            )}
          </div>

          <p className="text-[12px] text-subtle leading-snug max-w-xs">
            {t('av.hint')}
          </p>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-[12.5px] text-danger bg-danger-subtle rounded-card px-3 py-2.5 leading-snug">
          {error}
        </p>
      )}

      {/* Hidden, and driven by the button above — a bare file input cannot be
          styled and reads as a form somebody has to fill in. `capture` is
          deliberately absent: on a phone this offers the camera *and* the
          gallery, and most people already have the photograph they want. */}
      <input
        ref={inputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        className="hidden"
        onChange={(e) => void choose(e.target.files?.[0])}
      />
    </Card>
  );
}
